/**
 * EventsToolbar — filter controls above the events table.
 *
 * Three independent filters: type (all / feedback / error), feedback status
 * (only meaningful for type=feedback or type=all), and error source (only for
 * type=error or type=all). State is owned by the parent (EventsPage) so a
 * filter change can reset the in-memory page-state.
 */

import React from 'react';
import { Box, MenuItem, Stack, TextField } from '@mui/material';
import type { ListEventsParams } from '../../services/eventsApi';

export type TypeFilter = NonNullable<ListEventsParams['type']>;
export type StatusFilter = NonNullable<ListEventsParams['status']>;
export type SourceFilter = NonNullable<ListEventsParams['source']>;

interface Props {
  type: TypeFilter;
  status: StatusFilter;
  source: SourceFilter;
  onChange: (next: { type?: TypeFilter; status?: StatusFilter; source?: SourceFilter }) => void;
}

const EventsToolbar: React.FC<Props> = ({ type, status, source, onChange }) => {
  return (
    <Box mb={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          size="small"
          label="Typ"
          value={type}
          onChange={(e) => onChange({ type: e.target.value as TypeFilter })}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">Alle</MenuItem>
          <MenuItem value="feedback">Feedback</MenuItem>
          <MenuItem value="error">Fehler</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Feedback-Status"
          value={status}
          onChange={(e) => onChange({ status: e.target.value as StatusFilter })}
          disabled={type === 'error'}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">Alle</MenuItem>
          <MenuItem value="new">Neu</MenuItem>
          <MenuItem value="reviewed">Geprueft</MenuItem>
          <MenuItem value="resolved">Erledigt</MenuItem>
          <MenuItem value="wontfix">Wontfix</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Fehlerquelle"
          value={source}
          onChange={(e) => onChange({ source: e.target.value as SourceFilter })}
          disabled={type === 'feedback'}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">Alle</MenuItem>
          <MenuItem value="api">API</MenuItem>
          <MenuItem value="errorBoundary">UI-Fehler</MenuItem>
        </TextField>
      </Stack>
    </Box>
  );
};

export default EventsToolbar;
